import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Layers, ChevronDown, Plus, Filter, RefreshCw, 
  ArrowLeft, Settings, Download, Bookmark, X, MoreVertical,
  Pin, Pencil, Trash2, PinOff, Save, LayoutDashboard,
  AlertTriangle, GitBranch, Scale, Calendar, CheckCircle,
  Clock, Ban, Target, ExternalLink, Upload, Link2, FileSpreadsheet,
  FileText, ChevronRight, TrendingUp
} from 'lucide-react';
import { api } from '../api/client';
import { useFilters } from '../context/FilterContext';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { WorkItemComposer } from '../components/WorkItemComposer';
import { HierarchyTable } from '../components/HierarchyTable';
import { WorkItemEditor } from '../components/WorkItemEditor';
import { UpgradeBanner } from '../components/UpgradeBanner';
import PageHelpButtons from '../components/PageHelpButtons';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

/**
 * ProjectWorkItemsPage - Project Delivery Workspace
 * 
 * Phase 2: Transformed from Work Items into a complete delivery workspace
 * - Delivery Health Summary cards
 * - Tabbed workspace (Overview, Work Items, Risks & Issues, etc.)
 * - Source visibility (ScopeOps Native, Excel Managed, Jira Connected, etc.)
 * - Source-aware actions
 * - PM Insights panel
 * 
 * CORE PRINCIPLE: Reuses existing data and components - no new backend entities
 */

// Source type configuration
const SOURCE_CONFIG = {
  scopeops_native: { label: 'ScopeOps Native', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: CheckCircle },
  excel_managed: { label: 'Excel Managed', color: 'bg-green-100 text-green-700 border-green-200', icon: FileSpreadsheet },
  jira_connected: { label: 'Jira Connected', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Link2 },
  ado_connected: { label: 'ADO Connected', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Link2 },
  vendor_managed: { label: 'Vendor Managed', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: ExternalLink },
  custom_source: { label: 'Custom Source', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Settings }
};

// Delivery Health Summary Card Component
const HealthCard = ({ label, value, icon: Icon, color = 'blue', trend, onClick }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700'
  };
  
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col p-4 rounded-xl border ${colorClasses[color]} hover:shadow-md transition-all cursor-pointer text-left`}
      data-testid={`health-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium uppercase tracking-wide opacity-75">{label}</span>
        {Icon && <Icon className="w-4 h-4 opacity-50" />}
      </div>
      <span className="text-2xl font-bold">{value}</span>
      {trend && (
        <span className={`text-xs mt-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% from last week
        </span>
      )}
    </button>
  );
};

// PM Insight Card Component
const InsightCard = ({ insight, onAction }) => {
  const iconMap = {
    ban: Ban,
    clock: Clock,
    'git-branch': GitBranch,
    scale: Scale,
    'alert-triangle': AlertTriangle,
    target: Target,
    'check-circle': CheckCircle
  };
  const Icon = iconMap[insight.icon] || AlertTriangle;
  
  const typeColors = {
    critical: 'border-l-red-500 bg-red-50',
    warning: 'border-l-amber-500 bg-amber-50',
    info: 'border-l-blue-500 bg-blue-50',
    success: 'border-l-green-500 bg-green-50'
  };
  
  return (
    <div className={`p-3 rounded-lg border-l-4 ${typeColors[insight.type] || typeColors.info}`} data-testid="insight-card">
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-0.5 ${insight.type === 'critical' ? 'text-red-600' : insight.type === 'warning' ? 'text-amber-600' : insight.type === 'success' ? 'text-green-600' : 'text-blue-600'}`} />
        <div className="flex-1">
          <p className="font-medium text-sm text-gray-900">{insight.title}</p>
          <p className="text-xs text-gray-600 mt-0.5">{insight.description}</p>
          {insight.action && (
            <button
              type="button"
              onClick={() => onAction(insight.action_link)}
              className="text-xs text-blue-600 hover:text-blue-800 mt-1 font-medium"
            >
              {insight.action} →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Source Badge Component with Sync Status
const SourceBadge = ({ source }) => {
  if (!source) return null;
  const config = SOURCE_CONFIG[source.source_type] || SOURCE_CONFIG.scopeops_native;
  const Icon = config.icon;
  
  // Format last sync time
  const formatSyncTime = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };
  
  // Determine sync health based on time since last sync
  const getSyncHealth = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    
    if (diffHours < 1) return { status: 'healthy', color: 'text-green-600', label: 'Healthy' };
    if (diffHours < 24) return { status: 'ok', color: 'text-blue-600', label: 'OK' };
    if (diffHours < 72) return { status: 'stale', color: 'text-amber-600', label: 'Stale' };
    return { status: 'outdated', color: 'text-red-600', label: 'Outdated' };
  };
  
  const syncTime = formatSyncTime(source.last_sync);
  const syncHealth = getSyncHealth(source.last_sync);
  const isConnected = source.source_type !== 'scopeops_native';
  
  return (
    <div className="flex items-center gap-3" data-testid="source-badge">
      <Badge variant="outline" className={`gap-1.5 ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </Badge>
      
      {/* Sync Status Indicator for connected sources */}
      {isConnected && syncTime && (
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-gray-500">Last sync:</span>
          <span className="font-medium text-gray-700">{syncTime}</span>
          {syncHealth && (
            <span className={`font-medium ${syncHealth.color}`}>
              ({syncHealth.label})
            </span>
          )}
        </div>
      )}
      
      {/* For Excel Managed, show import time */}
      {source.source_type === 'excel_managed' && source.last_sync && (
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-gray-500">Imported:</span>
          <span className="font-medium text-gray-700">{syncTime}</span>
        </div>
      )}
      
      {/* External project reference */}
      {source.external_project && (
        <span className="text-xs text-gray-400 truncate max-w-[150px]" title={source.external_project}>
          ({source.external_project})
        </span>
      )}
    </div>
  );
};

// Source-Aware Actions Component
const SourceAwareActions = ({ source, projectId, onCreateWorkItem, onBulkCreate }) => {
  const navigate = useNavigate();
  
  switch (source?.source_type) {
    case 'excel_managed':
      return (
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => navigate(`/delivery/excel-intake?project=${projectId}`)}
            className="gap-2"
            data-testid="import-excel-btn"
          >
            <Upload className="w-4 h-4" />
            Import Excel
          </Button>
          <Button variant="outline" onClick={() => navigate(`/delivery/excel-intake?project=${projectId}&mode=reimport`)}>
            Re-Import
          </Button>
        </div>
      );
    case 'jira_connected':
      return (
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => navigate('/admin/integrations/jira')}
            className="gap-2"
            data-testid="sync-jira-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Sync Jira
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/integrations/jira')}>
            Manage Connection
          </Button>
        </div>
      );
    case 'ado_connected':
      return (
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => navigate('/admin/integrations/azure-devops')}
            className="gap-2"
            data-testid="sync-ado-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Sync ADO
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/integrations/azure-devops')}>
            Manage Connection
          </Button>
        </div>
      );
    default: // scopeops_native
      return (
        <div className="flex items-center gap-2">
          <Button onClick={onCreateWorkItem} className="gap-2" data-testid="create-work-item-btn">
            <Plus className="w-4 h-4" />
            Create Work Item
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="more-actions-btn">
                <ChevronDown className="w-4 h-4" />
                More
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onBulkCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Bulk Create
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate(`/delivery/excel-intake?project=${projectId}`)}>
                <Upload className="w-4 h-4 mr-2" />
                Import from Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
  }
};

const ProjectWorkItemsPage = () => {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { filters, getFilterParams } = useFilters();
  const { canEdit, tenant, isStoryLayerEnabled } = useTenant();
  const { user } = useAuth();
  
  // State
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || '');
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editorConfig, setEditorConfig] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [storyLayerEnabled, setStoryLayerEnabled] = useState(true);
  
  // Phase 2: Delivery Workspace state
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'work-items');
  const [deliverySummary, setDeliverySummary] = useState(null);
  const [insights, setInsights] = useState([]);
  const [traceability, setTraceability] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  
  // Sprint 24: Saved Views state
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(searchParams.get('view'));
  const [activeView, setActiveView] = useState(null);
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [editingView, setEditingView] = useState(null);
  const [newViewName, setNewViewName] = useState('');
  
  // TENANT ISOLATION: Use standardized hook for correct tenant ID
  const { tenantId } = useActiveTenant();
  
  // Phase 2: Load delivery summary, insights, and traceability
  useEffect(() => {
    const loadDeliverySummary = async () => {
      if (!selectedProjectId) return;
      
      setSummaryLoading(true);
      try {
        const [summaryRes, insightsRes, traceabilityRes] = await Promise.all([
          api.get(`/delivery-workspace/summary/${selectedProjectId}`),
          api.get(`/delivery-workspace/insights/${selectedProjectId}`),
          api.get(`/delivery-workspace/traceability/${selectedProjectId}`)
        ]);
        setDeliverySummary(summaryRes.data);
        setInsights(insightsRes.data?.insights || []);
        setTraceability(traceabilityRes.data?.traceability || null);
      } catch (error) {
        console.error('Failed to load delivery summary:', error);
      } finally {
        setSummaryLoading(false);
      }
    };
    
    loadDeliverySummary();
  }, [selectedProjectId, refreshKey]);
  
  // Phase 2: Handle tab changes with URL sync
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (tab === 'work-items') {
        newParams.delete('tab');
      } else {
        newParams.set('tab', tab);
      }
      return newParams;
    }, { replace: true });
  };
  
  // Phase 2: Handle insight action clicks
  const handleInsightAction = (actionLink) => {
    if (!actionLink) return;
    if (actionLink.startsWith('#')) {
      // Tab navigation
      handleTabChange(actionLink.slice(1));
    } else if (actionLink.startsWith('?')) {
      // Filter navigation
      setSearchParams(new URLSearchParams(actionLink.slice(1)));
    } else {
      // External navigation
      navigate(actionLink);
    }
  };
  
  // Sprint 24: Sync typeFilter with URL - ensures URL is single source of truth
  useEffect(() => {
    const urlType = searchParams.get('type') || 'all';
    if (urlType !== typeFilter) {
      setTypeFilter(urlType);
    }
    // Sync tab from URL
    const urlTab = searchParams.get('tab') || 'work-items';
    if (urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [searchParams]);
  
  // Sprint 24: Load saved views
  useEffect(() => {
    const loadSavedViews = async () => {
      try {
        const res = await api.get('/saved-views', { params: { pinned: true } });
        const views = res.data?.views || [];
        // Filter to work item related views
        const workItemViews = views.filter(v => 
          ['epics', 'features', 'stories', 'tasks', 'all'].includes(v.view_type)
        );
        setSavedViews(workItemViews);
        
        // If we have an active view ID from URL, find and apply it
        if (activeViewId) {
          const view = workItemViews.find(v => v.id === activeViewId);
          if (view) {
            setActiveView(view);
            if (view.view_type) setTypeFilter(view.view_type);
          }
        }
      } catch (error) {
        console.error('Failed to load saved views:', error);
      }
    };
    
    loadSavedViews();
  }, [activeViewId]);
  
  // Sprint 24: Handle view selection
  const handleSelectView = (view) => {
    if (view) {
      setActiveViewId(view.id);
      setActiveView(view);
      if (view.view_type) setTypeFilter(view.view_type);
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('view', view.id);
        if (view.view_type) newParams.set('type', view.view_type);
        return newParams;
      }, { replace: true });
    } else {
      // Clear view
      setActiveViewId(null);
      setActiveView(null);
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('view');
        return newParams;
      }, { replace: true });
    }
  };
  
  // Sprint 24: Handle type filter change
  const handleTypeChange = (newType) => {
    setTypeFilter(newType);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (newType === 'all') {
        newParams.delete('type');
      } else {
        newParams.set('type', newType);
      }
      return newParams;
    }, { replace: true });
  };
  
  // Sprint 24: Clear active filter chip
  const handleClearFilter = (filterKey) => {
    if (filterKey === 'type') {
      handleTypeChange('all');
    }
    if (filterKey === 'view') {
      handleSelectView(null);
    }
  };
  
  // Sprint 24: Delete saved view
  const handleDeleteView = async (viewId) => {
    try {
      await api.delete(`/saved-views/${viewId}`);
      setSavedViews(prev => prev.filter(v => v.id !== viewId));
      if (activeViewId === viewId) {
        handleSelectView(null);
      }
      toast.success('View deleted');
    } catch (error) {
      console.error('Failed to delete view:', error);
      toast.error('Failed to delete view');
    }
  };
  
  // Sprint 24: Rename saved view
  const handleRenameView = (view) => {
    setEditingView(view);
    setNewViewName(view.name);
    setRenameDialogOpen(true);
  };
  
  const handleSaveRename = async () => {
    if (!editingView || !newViewName.trim()) return;
    try {
      await api.patch(`/saved-views/${editingView.id}`, { name: newViewName.trim() });
      setSavedViews(prev => prev.map(v => 
        v.id === editingView.id ? { ...v, name: newViewName.trim() } : v
      ));
      if (activeView?.id === editingView.id) {
        setActiveView(prev => ({ ...prev, name: newViewName.trim() }));
      }
      toast.success('View renamed');
      setRenameDialogOpen(false);
      setEditingView(null);
    } catch (error) {
      console.error('Failed to rename view:', error);
      toast.error('Failed to rename view');
    }
  };
  
  // Sprint 24: Update saved view filters (save current filters to view)
  const handleUpdateViewFilters = async (view) => {
    try {
      const updates = {
        view_type: typeFilter !== 'all' ? typeFilter : view.view_type,
        filters: {
          ...view.filters,
          type: typeFilter !== 'all' ? typeFilter : null
        }
      };
      await api.patch(`/saved-views/${view.id}`, updates);
      setSavedViews(prev => prev.map(v => 
        v.id === view.id ? { ...v, ...updates } : v
      ));
      toast.success('View filters updated');
    } catch (error) {
      console.error('Failed to update view:', error);
      toast.error('Failed to update view filters');
    }
  };
  
  // Sprint 24: Unpin saved view
  const handleUnpinView = async (viewId) => {
    try {
      await api.patch(`/saved-views/${viewId}`, { is_pinned: false });
      setSavedViews(prev => prev.filter(v => v.id !== viewId));
      if (activeViewId === viewId) {
        handleSelectView(null);
      }
      toast.success('View unpinned');
      // Notify sidebar to refresh
      window.dispatchEvent(new CustomEvent('savedViewsUpdated'));
    } catch (error) {
      console.error('Failed to unpin view:', error);
      toast.error('Failed to unpin view');
    }
  };
  
  // Load projects list
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const res = await api.getProjects();
        const projectsList = res.data.projects || res.data || [];
        setProjects(projectsList);
        
        // Set initial project
        if (projectId) {
          setSelectedProjectId(projectId);
        } else if (projectsList.length > 0) {
          setSelectedProjectId(projectsList[0].id);
        }
      } catch (error) {
        console.error('Failed to load projects:', error);
      }
    };
    
    loadProjects();
  }, [projectId]);
  
  // Load project details and check story layer
  useEffect(() => {
    const loadProjectDetails = async () => {
      if (!selectedProjectId) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const [projectRes, configRes] = await Promise.all([
          api.getProjectDashboard(selectedProjectId, {}),
          tenantId ? api.getHierarchyConfig(tenantId) : Promise.resolve({ data: { story_layer_enabled: true } })
        ]);
        
        setProject(projectRes.data?.project || projectRes.data);
        setStoryLayerEnabled(configRes.data?.hierarchy_config?.enable_story_layer ?? configRes.data?.story_layer_enabled ?? true);
      } catch (error) {
        console.error('Failed to load project:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadProjectDetails();
  }, [selectedProjectId, tenantId]);
  
  // Handle project change - preserve type and other search params
  const handleProjectChange = (newProjectId) => {
    setSelectedProjectId(newProjectId);
    // Preserve current search params when changing projects
    const currentParams = searchParams.toString();
    const queryString = currentParams ? `?${currentParams}` : '';
    navigate(`/project/${newProjectId}/work-items${queryString}`, { replace: true });
  };
  
  // Handle composer success
  const handleComposerSuccess = (result) => {
    setShowComposer(false);
    setRefreshKey(prev => prev + 1);
    toast.success(`${result.created_count} items created`);
  };
  
  // Handle quick add from hierarchy table
  const handleQuickAdd = (parent, childType, onComplete) => {
    setEditorConfig({
      itemType: childType,
      parentId: parent.id,
      parentType: parent.type,
      projectId: selectedProjectId
    });
    setShowEditor(true);
  };
  
  // Handle editor save
  const handleEditorSave = () => {
    setShowEditor(false);
    setEditorConfig(null);
    setRefreshKey(prev => prev + 1);
  };
  
  // Handle bulk action
  const handleBulkAction = async (field, value, itemIds) => {
    try {
      await api.bulkUpdateWorkItems({
        item_ids: itemIds,
        item_type: 'work_item',
        updates: { [field]: value },
        tenant_id: tenantId
      });
      
      toast.success(`${itemIds.length} items updated`);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Bulk action failed:', error);
      toast.error('Failed to update items');
    }
  };
  
  // Render loading state
  if (loading && !project) {
    return (
      <div className="min-h-screen bg-[var(--clr-surface-page)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-[var(--clr-brand-primary)] border-t-transparent" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[var(--clr-surface-page)]">
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Phase 2: Project Delivery Workspace Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-[var(--clr-surface-hover)] rounded-lg transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5 text-[var(--clr-text-secondary)]" />
            </button>
            
            <div className="flex items-center gap-3 flex-1">
              <div className="p-2.5 bg-[var(--clr-brand-primary)] bg-opacity-10 rounded-xl">
                <LayoutDashboard className="w-6 h-6 text-[var(--clr-brand-primary)]" />
              </div>
              <div className="flex-1">
                {/* Priority 3: Show project name prominently */}
                <h1 className="text-2xl font-bold text-[var(--clr-text-primary)]" data-testid="workspace-title">
                  {deliverySummary?.project_name || projects.find(p => p.id === selectedProjectId)?.name || 'Project Delivery Workspace'}
                </h1>
                <p className="text-sm text-[var(--clr-text-muted)]">
                  Project Delivery Workspace
                </p>
              </div>
              
              {/* Source Badge */}
              {deliverySummary?.source && (
                <SourceBadge source={deliverySummary.source} />
              )}
            </div>
            
            {/* Support AI Buttons - P1 */}
            <PageHelpButtons 
              mode="advisory"
              variant="compact"
              projectId={selectedProjectId}
            />
          </div>
          
          {/* Project selector and source-aware actions */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-[var(--clr-text-secondary)]">Project:</label>
                <select
                  value={selectedProjectId}
                  onChange={(e) => handleProjectChange(e.target.value)}
                  className="px-4 py-2 bg-white border border-[var(--clr-border-light)] rounded-lg text-[var(--clr-text-primary)] focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-transparent font-medium"
                  data-testid="project-selector"
                >
                  <option value="">Select Project...</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Source-Aware Actions */}
            {canEdit && selectedProjectId && (
              <SourceAwareActions 
                source={deliverySummary?.source}
                projectId={selectedProjectId}
                onCreateWorkItem={() => setShowComposer(true)}
                onBulkCreate={() => setShowComposer(true)}
              />
            )}
          </div>
          
          {/* Phase 2: Delivery Health Summary Cards */}
          {selectedProjectId && deliverySummary && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="health-summary">
              <HealthCard
                label="Work Items"
                value={deliverySummary.summary.total_work_items}
                icon={Layers}
                color="blue"
                onClick={() => handleTabChange('work-items')}
              />
              <HealthCard
                label="In Progress"
                value={deliverySummary.summary.in_progress}
                icon={Clock}
                color="amber"
                onClick={() => {
                  // Combine tab and status updates into single atomic operation
                  setSearchParams(prev => {
                    const newParams = new URLSearchParams(prev);
                    newParams.set('tab', 'work-items');
                    newParams.set('status', 'In Progress');
                    return newParams;
                  }, { replace: true });
                  setActiveTab('work-items');
                }}
              />
              <HealthCard
                label="Blocked"
                value={deliverySummary.summary.blocked}
                icon={Ban}
                color={deliverySummary.summary.blocked > 0 ? 'red' : 'gray'}
                onClick={() => {
                  // Combine tab and status updates into single atomic operation
                  setSearchParams(prev => {
                    const newParams = new URLSearchParams(prev);
                    newParams.set('tab', 'work-items');
                    newParams.set('status', 'Blocked');
                    return newParams;
                  }, { replace: true });
                  setActiveTab('work-items');
                }}
              />
              <HealthCard
                label="Behind Schedule"
                value={deliverySummary.summary.behind_schedule}
                icon={AlertTriangle}
                color={deliverySummary.summary.behind_schedule > 0 ? 'red' : 'gray'}
                onClick={() => handleTabChange('work-items')}
              />
              <HealthCard
                label="Open Risks"
                value={deliverySummary.summary.open_risks}
                icon={AlertTriangle}
                color={deliverySummary.summary.open_risks > 0 ? 'amber' : 'gray'}
                onClick={() => handleTabChange('risks-issues')}
              />
              <HealthCard
                label="Open Decisions"
                value={deliverySummary.summary.open_decisions}
                icon={Scale}
                color={deliverySummary.summary.open_decisions > 0 ? 'purple' : 'gray'}
                onClick={() => handleTabChange('decisions')}
              />
            </div>
          )}
          
          {/* PM Insights Panel (when there are insights to show) */}
          {insights.length > 0 && insights[0].type !== 'success' && (
            <div className="bg-white rounded-xl border border-[var(--clr-border-light)] p-4 mb-6" data-testid="pm-insights">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Target className="w-4 h-4" />
                PM Insights
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {insights.slice(0, 4).map((insight, idx) => (
                  <InsightCard 
                    key={idx} 
                    insight={insight} 
                    onAction={handleInsightAction}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Phase 2: Workspace Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="mb-6 bg-[var(--clr-surface-card)] border border-[var(--clr-border-light)] p-1 h-auto flex-wrap">
            <TabsTrigger value="overview" className="gap-2 data-[state=active]:bg-[var(--clr-brand-primary)] data-[state=active]:text-white" data-testid="tab-overview">
              <LayoutDashboard className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="work-items" className="gap-2 data-[state=active]:bg-[var(--clr-brand-primary)] data-[state=active]:text-white" data-testid="tab-work-items">
              <Layers className="w-4 h-4" />
              Work Items
            </TabsTrigger>
            <TabsTrigger value="risks-issues" className="gap-2 data-[state=active]:bg-[var(--clr-brand-primary)] data-[state=active]:text-white" data-testid="tab-risks-issues">
              <AlertTriangle className="w-4 h-4" />
              Risks & Issues
            </TabsTrigger>
            <TabsTrigger value="dependencies" className="gap-2 data-[state=active]:bg-[var(--clr-brand-primary)] data-[state=active]:text-white" data-testid="tab-dependencies">
              <GitBranch className="w-4 h-4" />
              Dependencies
            </TabsTrigger>
            <TabsTrigger value="decisions" className="gap-2 data-[state=active]:bg-[var(--clr-brand-primary)] data-[state=active]:text-white" data-testid="tab-decisions">
              <Scale className="w-4 h-4" />
              Decisions
            </TabsTrigger>
            <TabsTrigger value="releases" className="gap-2 data-[state=active]:bg-[var(--clr-brand-primary)] data-[state=active]:text-white" data-testid="tab-releases">
              <Calendar className="w-4 h-4" />
              Releases & Sprints
            </TabsTrigger>
          </TabsList>
          
          {/* Overview Tab Content */}
          <TabsContent value="overview" className="mt-0">
            <div className="bg-white rounded-xl border border-[var(--clr-border-light)] p-6">
              <h3 className="text-lg font-semibold mb-4">Delivery Overview</h3>
              
              {deliverySummary && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Progress Summary */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-700">Progress Summary</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Completion</span>
                        <span className="font-semibold">{deliverySummary.summary.completion_pct}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className="bg-[var(--clr-brand-primary)] h-2.5 rounded-full transition-all" 
                          style={{ width: `${deliverySummary.summary.completion_pct}%` }}
                        />
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Completed:</span>
                          <span className="ml-2 font-medium">{deliverySummary.summary.completed}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Remaining:</span>
                          <span className="ml-2 font-medium">{deliverySummary.summary.total_work_items - deliverySummary.summary.completed}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Health Status */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-700">Health Status</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(deliverySummary.health_indicators).map(([key, status]) => (
                        <div key={key} className={`p-3 rounded-lg border ${
                          status === 'on_track' ? 'bg-green-50 border-green-200' :
                          status === 'warning' ? 'bg-amber-50 border-amber-200' :
                          'bg-red-50 border-red-200'
                        }`}>
                          <div className="flex items-center gap-2">
                            {status === 'on_track' ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : status === 'warning' ? (
                              <AlertTriangle className="w-4 h-4 text-amber-600" />
                            ) : (
                              <Ban className="w-4 h-4 text-red-600" />
                            )}
                            <span className="text-sm font-medium capitalize">{key.replace('_', ' ')}</span>
                          </div>
                          <span className={`text-xs mt-1 ${
                            status === 'on_track' ? 'text-green-700' :
                            status === 'warning' ? 'text-amber-700' :
                            'text-red-700'
                          }`}>
                            {status === 'on_track' ? 'On Track' : status === 'warning' ? 'Needs Attention' : 'At Risk'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Governance Traceability Panel */}
              {traceability && (
                <div className="mt-6 pt-6 border-t" data-testid="traceability-panel">
                  <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Governance Traceability
                  </h4>
                  
                  {/* Governance Chain Visualization */}
                  <div className="mb-6 p-4 bg-gradient-to-r from-slate-50 to-white rounded-lg border border-slate-200" data-testid="governance-chain">
                    <div className="flex items-center justify-center gap-1 text-xs flex-wrap">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full font-medium">
                        <FileText className="w-3.5 h-3.5" />
                        <span>{traceability.requirements?.total || 0} Reqs</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-100 text-indigo-800 rounded-full font-medium">
                        <Layers className="w-3.5 h-3.5" />
                        <span>{traceability.work_items?.total || 0} Work Items</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full font-medium">
                        <Scale className="w-3.5 h-3.5" />
                        <span>{traceability.decisions?.total || 0} Decisions</span>
                        {traceability.decisions?.linked > 0 && (
                          <span className="ml-1 text-purple-600">({traceability.decisions.linked} linked)</span>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-800 rounded-full font-medium">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        <span>{traceability.risks?.total || 0} Risks</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 rounded-full font-medium">
                        <TrendingUp className="w-3.5 h-3.5" />
                        <span>{traceability.benefits?.total || 0} Benefits</span>
                      </div>
                    </div>
                    <p className="text-center text-xs text-gray-500 mt-3">
                      Complete governance chain: Requirements drive delivery, decisions govern changes, risks are managed, benefits are tracked
                    </p>
                  </div>
                  
                  <p className="text-xs text-gray-500 mb-4">
                    Understand what is connected and what could be impacted by delivery changes.
                  </p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {/* Requirements Linked */}
                    <button
                      type="button"
                      onClick={() => navigate(`/governance/requirements?project=${selectedProjectId}`)}
                      className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-left hover:bg-blue-100 transition-colors"
                      data-testid="trace-requirements"
                    >
                      <div className="text-xs text-blue-600 font-medium uppercase tracking-wide mb-1">Requirements</div>
                      <div className="text-2xl font-bold text-blue-800">{traceability.requirements?.total || 0}</div>
                      <div className="text-xs text-blue-600 mt-1">
                        {traceability.requirements?.linked_to_work_items || 0} linked to work
                      </div>
                    </button>
                    
                    {/* Work Items */}
                    <button
                      type="button"
                      onClick={() => handleTabChange('work-items')}
                      className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-left hover:bg-indigo-100 transition-colors"
                      data-testid="trace-work-items"
                    >
                      <div className="text-xs text-indigo-600 font-medium uppercase tracking-wide mb-1">Work Items</div>
                      <div className="text-2xl font-bold text-indigo-800">{traceability.work_items?.total || 0}</div>
                      <div className="text-xs text-indigo-600 mt-1">
                        {traceability.work_items?.with_requirements || 0} with requirements
                      </div>
                    </button>
                    
                    {/* Decisions */}
                    <button
                      type="button"
                      onClick={() => handleTabChange('decisions')}
                      className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-left hover:bg-purple-100 transition-colors"
                      data-testid="trace-decisions"
                    >
                      <div className="text-xs text-purple-600 font-medium uppercase tracking-wide mb-1">Decisions</div>
                      <div className="text-2xl font-bold text-purple-800">{traceability.decisions?.total || 0}</div>
                      <div className="text-xs text-purple-600 mt-1">
                        {traceability.decisions?.linked || 0} linked to delivery
                      </div>
                    </button>
                    
                    {/* Risks */}
                    <button
                      type="button"
                      onClick={() => handleTabChange('risks-issues')}
                      className="p-3 bg-red-50 border border-red-200 rounded-lg text-left hover:bg-red-100 transition-colors"
                      data-testid="trace-risks"
                    >
                      <div className="text-xs text-red-600 font-medium uppercase tracking-wide mb-1">Risks</div>
                      <div className="text-2xl font-bold text-red-800">{traceability.risks?.total || 0}</div>
                      <div className="text-xs text-red-600 mt-1">
                        {traceability.risks?.mitigated || 0} mitigated
                      </div>
                    </button>
                    
                    {/* Benefits */}
                    <button
                      type="button"
                      onClick={() => navigate(`/governance/benefits?project=${selectedProjectId}`)}
                      className="p-3 bg-green-50 border border-green-200 rounded-lg text-left hover:bg-green-100 transition-colors"
                      data-testid="trace-benefits"
                    >
                      <div className="text-xs text-green-600 font-medium uppercase tracking-wide mb-1">Benefits</div>
                      <div className="text-2xl font-bold text-green-800">{traceability.benefits?.total || 0}</div>
                      <div className="text-xs text-green-600 mt-1">
                        {traceability.benefits?.on_track || 0} on track
                      </div>
                    </button>
                  </div>
                  
                  {/* Traceability Flow Indicator */}
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                      <span className="font-medium">Requirement</span>
                      <span>→</span>
                      <span className="font-medium">Work Item</span>
                      <span>→</span>
                      <span className="font-medium">Decision</span>
                      <span>→</span>
                      <span className="font-medium">Risk</span>
                      <span>→</span>
                      <span className="font-medium">Benefit</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Quick Links */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Actions</h4>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleTabChange('work-items')}>
                    View Work Items
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleTabChange('risks-issues')}>
                    View Risks & Issues
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleTabChange('dependencies')}>
                    View Dependencies
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigate(`/governance/benefits?project=${selectedProjectId}`)}>
                    View Benefits
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Work Items Tab Content */}
          <TabsContent value="work-items" className="mt-0">
            {/* Original Work Items Content */}
            <div className="bg-white rounded-xl border border-[var(--clr-border-light)] p-4">
              {/* Sprint 24: Filter chips and saved views controls */}
              <div className="flex items-center gap-3 flex-wrap mb-4">
                {/* Type filter tabs */}
                <div className="flex items-center gap-1 bg-[var(--clr-surface-card)] rounded-lg p-1 border border-[var(--clr-border-light)]">
                  {['all', 'epics', 'features', 'stories', 'tasks'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleTypeChange(type)}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        typeFilter === type
                          ? 'bg-[var(--clr-brand-primary)] text-white'
                          : 'text-[var(--clr-text-secondary)] hover:bg-[var(--clr-surface-hover)]'
                      }`}
                      data-testid={`type-filter-${type}`}
                    >
                      {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
                
                {/* Saved Views dropdown */}
                {savedViews.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-2" data-testid="saved-views-dropdown">
                        <Bookmark className="w-4 h-4" />
                        Saved Views
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-64">
                      {activeView && (
                        <>
                          <DropdownMenuItem onClick={() => handleSelectView(null)}>
                            <X className="w-4 h-4 mr-2" />
                            Clear saved view
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      {savedViews.map((view) => (
                        <DropdownMenuItem 
                          key={view.id}
                          onClick={() => handleSelectView(view)}
                          className={activeViewId === view.id ? 'bg-blue-50' : ''}
                        >
                          <Pin className="w-4 h-4 mr-2 text-blue-500 flex-shrink-0" />
                          <span className="flex-1 truncate">{view.name}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                
                {/* Active filter chips */}
                {(typeFilter !== 'all' || activeView) && (
                  <div className="flex items-center gap-2">
                    {typeFilter !== 'all' && (
                      <Badge variant="outline" className="gap-1 pl-2 bg-purple-50 text-purple-700 border-purple-200" data-testid="type-filter-chip">
                        Type = {typeFilter.charAt(0).toUpperCase() + typeFilter.slice(1)}
                        <button 
                          type="button"
                          onClick={() => handleClearFilter('type')} 
                          className="ml-1 hover:text-red-500"
                          data-testid="clear-type-filter"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    )}
                    {activeView && (
                      <Badge variant="outline" className="gap-1 pl-2 bg-blue-50">
                        <Pin className="w-3 h-3" />
                        {activeView.name}
                        <button 
                          type="button"
                          onClick={() => handleClearFilter('view')} 
                          className="ml-1 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    )}
                  </div>
                )}
              </div>
              
              {/* Work Item Composer */}
              {showComposer && selectedProjectId && (
                <div className="mb-4">
                  <WorkItemComposer
                    projectId={selectedProjectId}
                    tenantId={tenantId}
                    userId={user?.id}
                    storyLayerEnabled={storyLayerEnabled}
                    onSuccess={handleComposerSuccess}
                    onCancel={() => setShowComposer(false)}
                  />
                </div>
              )}
              
              {/* Hierarchy Table */}
              {selectedProjectId ? (
                <HierarchyTable
                  key={`${refreshKey}-${typeFilter}`}
                  projectId={selectedProjectId}
                  tenantId={tenantId}
                  typeFilter={typeFilter}
                  onQuickAdd={handleQuickAdd}
                  onBulkAction={handleBulkAction}
                />
              ) : (
                <div className="p-12 text-center">
                  <Layers className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Select a Project</h2>
                  <p className="text-gray-500">Choose a project from the dropdown above to view work items</p>
                </div>
              )}
            </div>
          </TabsContent>
          
          {/* Risks & Issues Tab Content */}
          <TabsContent value="risks-issues" className="mt-0">
            <div className="bg-white rounded-xl border border-[var(--clr-border-light)] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Risks & Issues</h3>
                <Button variant="outline" onClick={() => navigate(`/governance/risks?project=${selectedProjectId}`)}>
                  Open Full View
                </Button>
              </div>
              {selectedProjectId ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500">
                    View and manage risks and issues for this project.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <span className="font-medium text-red-900">Open Risks</span>
                      </div>
                      <span className="text-2xl font-bold text-red-700">{deliverySummary?.summary?.open_risks || 0}</span>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        <span className="font-medium text-amber-900">Open Issues</span>
                      </div>
                      <span className="text-2xl font-bold text-amber-700">{deliverySummary?.summary?.open_issues || 0}</span>
                    </div>
                  </div>
                  <Button onClick={() => navigate(`/governance/risks?project=${selectedProjectId}`)} className="w-full">
                    Manage Risks & Issues
                  </Button>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Select a project to view risks and issues</p>
              )}
            </div>
          </TabsContent>
          
          {/* Dependencies Tab Content */}
          <TabsContent value="dependencies" className="mt-0">
            <div className="bg-white rounded-xl border border-[var(--clr-border-light)] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Dependencies</h3>
                <Button variant="outline" onClick={() => navigate(`/governance/dependencies?project=${selectedProjectId}`)}>
                  Open Full View
                </Button>
              </div>
              {selectedProjectId ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500">
                    Track dependencies that may impact delivery.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <GitBranch className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-blue-900">Total Dependencies</span>
                      </div>
                      <span className="text-2xl font-bold text-blue-700">{deliverySummary?.summary?.dependencies || 0}</span>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <span className="font-medium text-red-900">At Risk</span>
                      </div>
                      <span className="text-2xl font-bold text-red-700">{deliverySummary?.summary?.dependencies_at_risk || 0}</span>
                    </div>
                  </div>
                  <Button onClick={() => navigate(`/governance/dependencies?project=${selectedProjectId}`)} className="w-full">
                    Manage Dependencies
                  </Button>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Select a project to view dependencies</p>
              )}
            </div>
          </TabsContent>
          
          {/* Decisions Tab Content */}
          <TabsContent value="decisions" className="mt-0">
            <div className="bg-white rounded-xl border border-[var(--clr-border-light)] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Decisions</h3>
                <Button variant="outline" onClick={() => navigate(`/governance/decisions?project=${selectedProjectId}`)}>
                  Open Full View
                </Button>
              </div>
              {selectedProjectId ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500">
                    Track project decisions that impact delivery.
                  </p>
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Scale className="w-5 h-5 text-purple-600" />
                      <span className="font-medium text-purple-900">Open Decisions</span>
                    </div>
                    <span className="text-2xl font-bold text-purple-700">{deliverySummary?.summary?.open_decisions || 0}</span>
                  </div>
                  <Button onClick={() => navigate(`/governance/decisions?project=${selectedProjectId}`)} className="w-full">
                    Manage Decisions
                  </Button>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Select a project to view decisions</p>
              )}
            </div>
          </TabsContent>
          
          {/* Releases & Sprints Tab Content */}
          <TabsContent value="releases" className="mt-0">
            <div className="bg-white rounded-xl border border-[var(--clr-border-light)] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Releases & Sprints</h3>
                <Button variant="outline" onClick={() => navigate(`/releases?project=${selectedProjectId}`)}>
                  Open Full View
                </Button>
              </div>
              {selectedProjectId ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500">
                    Manage release planning and sprint tracking.
                  </p>
                  <div className="flex gap-2">
                    <Button onClick={() => navigate(`/releases?project=${selectedProjectId}`)} variant="outline">
                      <Calendar className="w-4 h-4 mr-2" />
                      View Releases
                    </Button>
                    <Button onClick={() => navigate(`/sprints?project=${selectedProjectId}`)} variant="outline">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      View Sprints
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Select a project to view releases</p>
              )}
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Work Item Editor Modal */}
        {showEditor && editorConfig && (
          <WorkItemEditor
            isOpen={showEditor}
            onClose={() => {
              setShowEditor(false);
              setEditorConfig(null);
            }}
            itemType={editorConfig.itemType}
            parentId={editorConfig.parentId}
            projectId={editorConfig.projectId}
            tenantId={tenantId}
            onSave={handleEditorSave}
          />
        )}
        
        {/* Sprint 24: Rename View Dialog */}
        <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Rename Saved View</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="view-name">View Name</Label>
                <Input
                  id="view-name"
                  value={newViewName}
                  onChange={(e) => setNewViewName(e.target.value)}
                  placeholder="Enter view name"
                  data-testid="rename-view-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveRename} disabled={!newViewName.trim()} data-testid="save-rename-btn">
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ProjectWorkItemsPage;
